import React, { memo, useEffect, useRef, useState } from 'react'

// Libraries
import { PropTypes } from "prop-types";
import { m } from "framer-motion";
import { Link } from 'react-router-dom';

// Components
import Filter from './Filter';

// Context
import { useCart } from '../../contexts/CartContext';

// Data
import { PortfolioOverlayData } from './PortfolioData'

// Mapping couleurs vers codes hex
const COLOR_HEX = {
    'Blanc': '#FFFFFF',
    'Antique': '#8B7355',
    'Brun': '#654321',
    'Vert': '#228B22',
    'Bleu': '#4169E1',
    'Noir': '#1a1a1a',
    'Transparent': '#E8E8E8'
}

// Slugify pour l'URL de la gamme
const slugify = (str) =>
    str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '')

const PortfolioOverlay = ({ data = PortfolioOverlayData, hideRangeIndicator = false, ...props }) => {
    const portfolioWrapper = useRef()
    const [loading, setLoading] = useState(true)
    const isotopeRef = useRef(null)
    const { addToCart, removeFromCart, isInCart } = useCart()
    // State pour les images actives par produit (quand on clique sur une pastille couleur)
    const [activeColorImages, setActiveColorImages] = useState({})

    // Handler pour changer l'image quand on clique sur une pastille couleur
    const handleColorClick = (e, itemId, color, colorImages) => {
        e.preventDefault()
        e.stopPropagation()
        if (colorImages && colorImages[color]) {
            setActiveColorImages(prev => ({
                ...prev,
                [itemId]: colorImages[color]
            }))
        }
    }

    useEffect(() => {
        let isMounted = true;

        if (!portfolioWrapper.current) {
            setLoading(false);
            return;
        }

        let allImages = portfolioWrapper.current.querySelectorAll("img");

        Promise.all(Array.prototype.slice.call(allImages).filter(img => !img.complete).map(img => new Promise(resolve => { img.onload = img.onerror = resolve; }))).then(() => {
            if (!isMounted || !portfolioWrapper.current) {
                return;
            }
            import("../../Functions/Utilities").then(module => {
                if (!isMounted || !portfolioWrapper.current) {
                    return;
                }
                const grid = module.initializeIsotop(portfolioWrapper.current)
                if (grid) {
                    isotopeRef.current = grid
                    grid.on('arrangeComplete', () => {
                        if (isMounted) setLoading(false);
                    });
                } else {
                    if (isMounted) setLoading(false);
                }
            })
        });

        return () => {
            isMounted = false;
        };
    }, [])

    // Update layout when data changes
    useEffect(() => {
        if (isotopeRef.current && portfolioWrapper.current) {
            // Wait a bit for DOM to update
            const timer = setTimeout(() => {
                try {
                    isotopeRef.current.reloadItems()
                    isotopeRef.current.arrange({ filter: '*' })
                } catch (e) {
                    console.log('Isotope layout update skipped')
                }
            }, 50)
            return () => clearTimeout(timer)
        }
    }, [data])

    const style = {
        "--overlay-color": typeof (props.overlay) === "object" ? `linear-gradient(to right top, ${props.overlay.map(item => item)})` : props.overlay,
    }

    const handleFilterChange = () => {
        portfolioWrapper.current.querySelectorAll("li").forEach(item => item.childNodes[0]?.classList.add("appear"))
    }

    const handleAddToCart = (e, item) => {
        e.preventDefault()
        e.stopPropagation()

        if (item.productData) {
            const inCart = isInCart(item.id)

            if (inCart) {
                // Si déjà dans le panier, on le retire
                removeFromCart(item.id)
            } else {
                // Sinon on l'ajoute
                addToCart(item.productData)
            }
        }
    }

    return (
        <div className="grid-wrapper">
            {/* Filter Start */}
            <Filter title={props.title} filterData={props.filterData} onFilterChange={handleFilterChange} />
            {/* Filter End */}

            {/* Grid Start */}
            <ul ref={portfolioWrapper} className={`grid-container text-center${props.grid ? ` ${props.grid}` : ""}${props.className ? ` ${props.className}` : ""}${loading ? " loading" : ""}`}>
                <li className="grid-sizer"></li>
                {
                    data.map((item, i) => {
                        const inCart = item.id ? isInCart(item.id) : false
                        const productData = item.productData || {}
                        const colors = productData.colors || []
                        const colorImages = productData.color_images || {}
                        const defaultColor = productData.default_color
                        const hasMultipleColors = colors.length > 1 && Object.keys(colorImages).length > 0

                        // Image à afficher : image sélectionnée > image couleur par défaut > image principale
                        const getDefaultImage = () => {
                            if (defaultColor && colorImages[defaultColor]) {
                                return colorImages[defaultColor]
                            }
                            return item.img
                        }
                        const displayImage = activeColorImages[item.id] || getDefaultImage()

                        return (
                            <li key={i} className={`grid-item${item.double_col ? " grid-item-double" : ""} ${item.category.toString().split(",").join(" ").toLowerCase()}`}>
                                <Link aria-label="link" target={props.target} to={item.link}>
                                    <m.div
                                        className="portfolio-overlay overflow-hidden"
                                        initial={{ opacity: 0 }}
                                        whileInView={!loading && { opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, ease: "easeOut" }}
                                    >
                                        <div className='portfolio-image relative' style={{...style, height: '450px'}}>
                                            {displayImage && <img className="w-full h-full object-cover transition-all duration-300" src={displayImage} alt="portfolio-overlay" />}

                                            {/* Bouton panier */}
                                            {item.productData && (
                                                <button
                                                    onClick={(e) => handleAddToCart(e, item)}
                                                    className={`absolute top-4 right-4 z-10 w-[45px] h-[45px] rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
                                                        inCart
                                                            ? 'bg-glass-accent text-white hover:bg-red-500'
                                                            : 'bg-white text-glass-accent hover:bg-glass-accent hover:text-white hover:scale-110'
                                                    }`}
                                                    style={{ border: 'none' }}
                                                    title={inCart ? 'Retirer de ma demande' : 'Ajouter à ma demande'}
                                                >
                                                    <i className={`${inCart ? 'line-icon-Close' : 'line-icon-Add-Bag'} text-[18px]`}></i>
                                                </button>
                                            )}

                                            {/* Sélecteur de couleur - haut gauche, parallélogrammes */}
                                            {hasMultipleColors && (
                                                <div className="absolute top-4 left-4 z-10 flex gap-1">
                                                    {colors.map((color) => {
                                                        if (!colorImages[color]) return null
                                                        const isActive = activeColorImages[item.id]
                                                            ? activeColorImages[item.id] === colorImages[color]
                                                            : (defaultColor === color)
                                                        return (
                                                            <button
                                                                key={color}
                                                                onClick={(e) => handleColorClick(e, item.id, color, colorImages)}
                                                                className={`w-[19px] h-[32px] transition-all duration-200 shadow-md hover:scale-110 ${
                                                                    isActive ? 'ring-2 ring-white scale-110' : ''
                                                                }`}
                                                                style={{
                                                                    backgroundColor: COLOR_HEX[color] || '#ccc',
                                                                    border: color === 'Blanc' || color === 'Transparent' ? '2px solid #ddd' : '2px solid rgba(255,255,255,0.5)',
                                                                    transform: `skewX(-12deg)${isActive ? ' scale(1.1)' : ''}`,
                                                                    borderRadius: '3px'
                                                                }}
                                                                title={color}
                                                            />
                                                        )
                                                    })}
                                                </div>
                                            )}

                                            {/* Indicateur gamme - centré en bas (caché sur la page gamme) */}
                                            {productData.range && !hideRangeIndicator && (
                                                <Link
                                                    to={`/gamme/${slugify(productData.range)}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 text-white/90 hover:text-white text-[12px] font-medium transition-all duration-200 flex items-center gap-1 whitespace-nowrap"
                                                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}
                                                    title={`Voir toute la gamme ${productData.range}`}
                                                >
                                                    <span>Voir la gamme</span>
                                                    <i className="fas fa-chevron-right text-[10px]"></i>
                                                </Link>
                                            )}

                                            <div className="portfolio-hover absolute font-serif justify-center flex flex-col py-[50px] text-center xl:py-[30px]">
                                                {item.title && <div className="font-serif text-white text-[15px] font-medium block m-0">{item.title}</div>}
                                                {item.subtitle && <div className="block text-white opacity-70 text-md mt-2">{item.subtitle}</div>}
                                            </div>
                                        </div>
                                    </m.div>
                                </Link>
                            </li>
                        )
                    })
                }
            </ul>
            {/* Grid End */}
        </div>
    )
}

PortfolioOverlay.propTypes = {
    grid: PropTypes.string,
    hideRangeIndicator: PropTypes.bool,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            subtitle: PropTypes.string,
            title: PropTypes.string,
            img: PropTypes.string,
            category: PropTypes.array,
            link: PropTypes.string,
            double_col: PropTypes.bool,
            productData: PropTypes.object
        })
    )
}

export default memo(PortfolioOverlay)